import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Row, Col, Spinner } from "react-bootstrap";
import userApi from "../../services/userapi";
import { showConfirm } from "react-confirm-prompt";

const initialUserData = {
    username: "",
    password: "",
    name: "",
    branchId: "",
    userType: 0,
    employeeId: "",
};

const UserCreateForm = ({ show, handleClose, handleRefresh, editingUserId, branches, employees }) => {
    const [formData, setFormData] = useState(initialUserData);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [errors, setErrors] = useState({});
    const appUserTypes = [1, 2, 3, 4];
    const isAppUser = appUserTypes.includes(Number(formData.userType));

    const isEditing = !!editingUserId;

    useEffect(() => {
        if (isEditing) {
            fetchUserDetails();
        } else {
            setFormData(initialUserData);
            setErrors({});
        }
    }, [editingUserId]);

    const fetchUserDetails = async () => {
        setLoadingDetails(true);
        try {
            const response = await userApi.UserDetails({ id: editingUserId });
            if (response.status === "SUCCESS") {
                const data = response.data.user;
                setFormData({
                    username: data.userName || "",
                    name: data.name || "",
                    branchId: data.branchId || "",
                    userType: data.userType || 0,
                    employeeId: data.employeeId || "",
                    password: "", // Leave password empty when editing
                });
            } else {
                throw new Error(response.message || "Failed to fetch user details");
            }
        } catch (error) {
            showConfirm("Error", {
                description: error.message || "Unable to load user data.",
                type: "danger",
                hideCancel: true,
            });
        } finally {
            setLoadingDetails(false);
        }
    };

    const validate = () => {
        let errs = {};
        if (!formData.username.trim()) errs.username = "Username is required";
        if (!formData.name.trim()) errs.name = "Name is required";
        if (!isEditing && !formData.password) errs.password = "Password is required";
        if (formData.branchId === "" || formData.branchId === null || formData.branchId === undefined) {
            errs.branchId = "Branch is required";
        }
        if (!formData.employeeId) errs.employeeId = "Employee is required";
        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = { ...formData };
            if (isEditing) {
                payload.id = editingUserId;
                const response = await userApi.createUser(payload); // Reuse create API for update
                showConfirm("Success", {
                    description: response.message || "User updated successfully.",
                    type: "success",
                    hideCancel: true,
                });
            } else {
                const response = await userApi.createUser(payload);
                showConfirm("Success", {
                    description: response.message || "User created successfully.",
                    type: "success",
                    hideCancel: true,
                });
            }

            handleClose();
            handleRefresh();
        } catch (error) {
            showConfirm("Error", {
                description: error.message || "Something went wrong.",
                type: "danger",
                hideCancel: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: null }));
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? "Edit User" : "Create User"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingDetails ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <div className="mt-2">Loading user details...</div>
                        </div>
                    ) : (
                        <>
                            <Row className="mb-3">
                                <Col>
                                    <Form.Group>
                                        <Form.Label>Username</Form.Label>
                                        <Form.Control
                                            name="username"
                                            value={formData.username}
                                            onChange={handleChange}
                                            isInvalid={!!errors.username}
                                            disabled={loadingDetails}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.username}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                {!isEditing && (
                                    <Col>
                                        <Form.Group>
                                            <Form.Label>Password</Form.Label>
                                            <Form.Control
                                                name="password"
                                                type="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                isInvalid={!!errors.password}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.password}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                )}
                            </Row>

                            <Row className="mb-3">
                                <Col>
                                    <Form.Group>
                                        <Form.Label>Name</Form.Label>
                                        <Form.Control
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            isInvalid={!!errors.name}
                                            disabled={loadingDetails}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.name}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group>
                                        <Form.Label>Branch</Form.Label>
                                        <Form.Control
                                            as="select"
                                            name="branchId"
                                            value={formData.branchId?.toString() || ""}
                                            onChange={handleChange}
                                            isInvalid={!!errors.branchId}
                                            disabled={loadingDetails}
                                        >
                                            <option value="">Select Branch</option>
                                            <option value="0">All Branch</option>
                                            {branches.map((branch) => (
                                                <option key={branch.branchId} value={branch.branchId.toString()}>
                                                    {branch.branchName}
                                                </option>
                                            ))}
                                        </Form.Control>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.branchId}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className="mb-3">
                                <Col>
                                    <Form.Group>
                                        <Form.Label>User Type</Form.Label>
                                        <Form.Select
                                            name="userType"
                                            value={formData.userType}
                                            onChange={handleChange}
                                            disabled={loadingDetails}
                                        >
                                            <option value={0}>Web Admin</option>
                                            <option value={1}>Delivery App</option>
                                            <option value={2}>Picker App</option>
                                            <option value={3}>Packer App</option>
                                            <option value={4}>Picker + Packer App</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group>
                                        <Form.Label>Employee</Form.Label>
                                        <Form.Control
                                            as="select"
                                            name="employeeId"
                                            value={formData.employeeId}
                                            onChange={handleChange}
                                            isInvalid={!!errors.employeeId}
                                            disabled={loadingDetails}
                                        >
                                            <option value="">Select Employee</option>
                                            {employees.map((emp) => (
                                                <option key={emp.id} value={emp.id}>
                                                    {emp.name}
                                                </option>
                                            ))}
                                        </Form.Control>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.employeeId}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={loadingDetails || isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting || loadingDetails}
                    >
                        {isSubmitting
                            ? isEditing ? "Updating..." : "Creating..."
                            : isEditing ? "Update" : "Create"}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default UserCreateForm;
